import type { ProgramSourceState, ProgramSourceStatus } from "@/services/adminProgramNews";

const MAX_RAW_CHARS = 96;

/** Probe errors that mean "we reached the site but nothing there looked like a golf feed". */
const NO_FEED_PATTERNS = [
  /HTTP 404/i,
  /non-JSON/i,
  /not a sidearm archive/i,
  /\bno\b.*\bstories\b/i,
];

/** Transport-level failures: DNS, refused/reset connections, timeouts. */
const UNREACHABLE_PATTERNS = [
  /time(d)?\s?out/i,
  /ECONN/i,
  /ENOTFOUND/i,
  /EAI_AGAIN/i,
  /ETIMEDOUT/i,
  /fetch failed/i,
  /network/i,
  /socket/i,
];

export const REASON_NO_ATHLETICS_SITE = "No athletics website found for this college.";
export const REASON_NO_FEED = "Site found, but no golf news feed recognized.";
export const REASON_UNREACHABLE = "Site didn't respond.";
export const REASON_OVERRIDE_STALE = "Override URL stopped working.";

function truncate(value: string): string {
  return value.length > MAX_RAW_CHARS ? `${value.slice(0, MAX_RAW_CHARS - 1)}…` : value;
}

/**
 * One short human sentence explaining why a row is not verified.
 * Returns null when there is nothing to explain (successful attempt, never fetched).
 */
export function describeSourceReason(source: {
  status: ProgramSourceStatus;
  state: ProgramSourceState | null;
}): string | null {
  if (source.status === "override_stale") return REASON_OVERRIDE_STALE;

  const attempt = source.state?.lastAttempt ?? null;
  const error = attempt?.error?.trim() || "";

  if (!error) {
    return source.status === "failed" ? REASON_NO_ATHLETICS_SITE : null;
  }
  if (attempt?.ok) return null;

  if (error === "no-athletics-root") return REASON_NO_ATHLETICS_SITE;
  if (NO_FEED_PATTERNS.some(re => re.test(error))) return REASON_NO_FEED;
  if (UNREACHABLE_PATTERNS.some(re => re.test(error))) return REASON_UNREACHABLE;
  return truncate(error);
}
